import { useEffect } from 'react';
import { indexRobots, privateRobots, isProductionHost, socialImage } from '../seo/site';

interface SeoHelmetProps {
    title: string;
    description: string;
    canonicalUrl: string;
    image?: string;
    type?: string;
    publishedTime?: string;
    schema?: object;
    robots?: string;
    imageAlt?: string;
}

// Update the existing document tags instead of duplicating the static HTML metadata.
export default function SeoHelmet({title, description, canonicalUrl, image = socialImage, type = 'website', publishedTime, schema, robots = indexRobots, imageAlt = 'Radian Soul'}: SeoHelmetProps) {
    const schemaText = schema ? JSON.stringify(schema) : undefined;
    useEffect(() => {
        document.title = title;
        function meta(attribute: 'name' | 'property', key: string, content?: string) {
            const matches = Array.from(document.head.querySelectorAll<HTMLMetaElement>(`meta[${attribute}="${key}"]`));
            const tag = matches.shift();
            matches.forEach(element => element.remove());
            if (!content) { tag?.remove(); return; }
            const element = tag || document.createElement('meta');
            element.setAttribute(attribute, key);
            element.content = content;
            if (!tag) document.head.appendChild(element);
        }
        meta('name', 'description', description);
        meta('name', 'robots', isProductionHost(window.location.hostname) ? robots : privateRobots);
        meta('property', 'og:locale', 'en_IN');
        meta('property', 'og:image:alt', imageAlt);
        meta('name', 'twitter:image:alt', imageAlt);
        for (const [key, value] of Object.entries({'site_name':'Radian Soul',title,description,url:canonicalUrl,type,image})) meta('property', `og:${key}`, value);
        for (const [key,value] of Object.entries({card:'summary_large_image',title,description,image,url:canonicalUrl})) {
            // The original HTML used property for Twitter tags; normalize to name.
            document.head.querySelectorAll(`meta[property="twitter:${key}"]`).forEach(tag => tag.remove());
            meta('name', `twitter:${key}`, value);
        }
        meta('property', 'article:published_time', publishedTime);
        meta('property', 'article:modified_time', publishedTime);
        const canonicals = Array.from(document.head.querySelectorAll<HTMLLinkElement>('link[rel="canonical"]'));
        const canonical = canonicals.shift() || document.createElement('link');
        canonicals.forEach(tag => tag.remove());
        canonical.rel = 'canonical';
        canonical.href = canonicalUrl;
        if (!canonical.parentNode) document.head.appendChild(canonical);
        document.getElementById('page-structured-data')?.remove();
        if (schemaText) {
            const script = document.createElement('script');
            script.id = 'page-structured-data';
            script.type = 'application/ld+json';
            script.textContent = schemaText;
            document.head.appendChild(script);
        }
    }, [title, description, canonicalUrl, image, type, publishedTime, schemaText, robots, imageAlt]);
    return null;
}
